import type { AccountAddress, AccountContact } from '@/api/commercial/accounts';
import type { ProposalPayment } from '@/api/commercial/proposals';
import type {
  PublicProposalAddress,
  PublicProposalData,
  PublicProposalItem,
  PublicProposalPayment,
} from '@/api/commercial/public-proposal';
import type {
  ICartItemBreakdown,
  ICartPaymentSummary,
  IProposalCartItem,
} from '@/types/commercial/cart';
import { NegotiationType } from '@/types/commercial/cart';
import type { CartSummaryCategories } from '@/utils/commercial/cart-summary-calculations';

function deriveUnitPrice(subtotalInCents: number, quantity: number): number {
  if (quantity <= 0) return 0;
  return Math.round(subtotalInCents / quantity);
}

export function adaptPublicProposalItems(items: PublicProposalItem[]): IProposalCartItem[] {
  return items.map((item, idx) => {
    const negotiationType = item.negotiationType as NegotiationType;
    const isMonthlyItem =
      negotiationType === NegotiationType.SALE_WITH_SIM_CARD ||
      negotiationType === NegotiationType.SIM_CARD ||
      negotiationType === NegotiationType.SERVICES;

    return {
      id: `public-item-${idx}`,
      proposalId: 'public',
      productId: item.productName,
      productDescription: item.productDescription,
      negotiationType,
      quantity: item.quantity,
      unitPriceCashInCents: item.unitPriceCashInCents,
      unitPriceInstallmentInCents: item.unitPriceInstallmentInCents,
      unitPriceMonthlyInCents: isMonthlyItem
        ? deriveUnitPrice(item.subtotalMonthlyInCents, item.quantity)
        : 0,
      unitPriceYearlyInCents: 0,
      unitPriceFixedInCents: 0,
      originalPriceCashInCents: 0,
      originalPriceInstallmentInCents: 0,
      originalPriceMonthlyInCents: 0,
      originalPriceYearlyInCents: 0,
      originalPriceFixedInCents: 0,
      discountPercent: item.discountPercent,
      connectivityDiscountPercent: 0,
      subtotalCashInCents: item.subtotalCashInCents,
      subtotalInstallmentInCents: item.subtotalInstallmentInCents,
      subtotalMonthlyInCents: item.subtotalMonthlyInCents,
      subtotalYearlyInCents: 0,
      subtotalFixedInCents: 0,
      appliedRange: null,
      createdAt: new Date().toISOString(),
    };
  });
}

export function adaptPublicPayment(payment: PublicProposalPayment | null): ProposalPayment | null {
  if (!payment) return null;
  return {
    paymentType: payment.paymentType,
    dueDay: payment.dueDay,
    installments: payment.installments,
    entradaInCents: 0,
    freteInCents: 0,
    shippingIncludedInDownPayment: false,
    hasDownPayment: false,
  };
}

export function adaptPublicContacts(contactName: string, sellerEmail: string): AccountContact[] {
  if (!contactName) return [];
  return [
    {
      id: 'public-contact',
      accountId: 'public',
      name: contactName,
      contractEnabled: false,
      positionOrRelation: '',
      contactFor: [],
      contactItems: sellerEmail
        ? [
            {
              id: 'public-contact-email',
              contact: sellerEmail,
              type: 'Email',
              preferredContact: { email: true },
            },
          ]
        : [],
    },
  ];
}

export function adaptPublicAddress(address: PublicProposalAddress | null): AccountAddress | null {
  if (!address) return null;
  return {
    id: 'public-address',
    accountId: 'public',
    city: address.city,
    state: address.state,
    street: address.street,
    district: address.district,
    number: address.number ?? '',
    zip_code: address.zipCode,
    complement: address.complement ?? undefined,
  };
}

export function adaptPublicBreakdown(items: PublicProposalItem[]): ICartItemBreakdown {
  const breakdown: ICartItemBreakdown = {
    products: { totalCashInCents: 0, totalInstallmentInCents: 0, totalItems: 0, totalQuantity: 0 },
    accessories: {
      totalCashInCents: 0,
      totalInstallmentInCents: 0,
      totalItems: 0,
      totalQuantity: 0,
    },
    connectivity: { totalMonthlyInCents: 0, totalItems: 0, totalQuantity: 0 },
    monthlyServices: { totalMonthlyInCents: 0, totalItems: 0, totalQuantity: 0 },
    yearlyServices: { totalYearlyInCents: 0, totalItems: 0, totalQuantity: 0 },
    fixedServices: { totalFixedInCents: 0, totalItems: 0, totalQuantity: 0 },
  };

  for (const item of items) {
    const nt = item.negotiationType as NegotiationType;
    const isProduct =
      nt === NegotiationType.SALE_WITH_SIM_CARD || nt === NegotiationType.SALE_WITHOUT_SIM_CARD;
    const hasConnectivity =
      nt === NegotiationType.SALE_WITH_SIM_CARD || nt === NegotiationType.SIM_CARD;

    if (isProduct) {
      breakdown.products.totalItems += 1;
      breakdown.products.totalQuantity += item.quantity;
    }
    if (nt === NegotiationType.ACCESSORY_SALE) {
      breakdown.accessories.totalItems += 1;
      breakdown.accessories.totalQuantity += item.quantity;
    }
    if (hasConnectivity) {
      breakdown.connectivity.totalItems += 1;
      breakdown.connectivity.totalQuantity += item.quantity;
      breakdown.connectivity.totalMonthlyInCents += item.subtotalMonthlyInCents;
    }
    if (nt === NegotiationType.SERVICES) {
      breakdown.monthlyServices.totalItems += 1;
      breakdown.monthlyServices.totalQuantity += item.quantity;
      breakdown.monthlyServices.totalMonthlyInCents += item.subtotalMonthlyInCents;
    }
  }

  return breakdown;
}

export function adaptPublicPaymentSummary(
  data: PublicProposalData,
  breakdown: ICartItemBreakdown,
  cartSummaryCategories: CartSummaryCategories,
): ICartPaymentSummary | null {
  if (!data.payment) return null;

  const summary: ICartPaymentSummary = {
    paymentType: data.payment.paymentType,
    items: breakdown,
    shippingInCents: 0,
    totalDiscountInCents: 0,
  };

  if (data.payment.paymentType === 'CASH') {
    summary.cashTotal = {
      totalInCents: data.totals.totalCashInCents,
      shippingInCents: 0,
    };
  } else {
    summary.installmentTotal = {
      totalFinanceableInCents: data.totals.totalInstallmentInCents,
      downPaymentLabel: '',
      downPaymentInCents: 0,
      installments: data.payment.installments,
      installmentBreakdown: null,
      totalInCents: data.totals.totalInstallmentInCents,
    };
  }

  if (cartSummaryCategories.connectivity.netInCents > 0) {
    summary.recurringConnectivity = {
      monthlyPaymentInCents: cartSummaryCategories.connectivity.netInCents,
      dueDay: data.payment.dueDay,
      totalInCents: cartSummaryCategories.connectivity.netInCents,
    };
  }
  if (cartSummaryCategories.monthlyServices.netInCents > 0) {
    summary.recurringMonthlyServices = {
      monthlyPaymentInCents: cartSummaryCategories.monthlyServices.netInCents,
      dueDay: data.payment.dueDay,
      totalInCents: cartSummaryCategories.monthlyServices.netInCents,
    };
  }

  return summary;
}
